import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';
const WEEK_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const PANEL_HEIGHT = 340; // 아래 공간이 부족한지 판단할 대략적인 패널 높이
const MONTH_LIST = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const YEAR_RANGE = 10; // min/max가 없을 때 연도 목록에 표시할 앞뒤 범위

/** 'YYYY-MM-DD' 문자열을 moment로 변환. 형식이 어긋나면 null */
const parseDate = (str) => {
  if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
  const date = moment(str, DATE_FORMAT);
  // 2월 31일처럼 없는 날짜는 다른 날로 넘어가므로 되돌려 비교한다
  return date.isValid() && date.format(DATE_FORMAT) === str ? date : null;
};

/** moment를 'YYYY-MM-DD' 문자열로 변환 */
const formatDate = (date) => date.format(DATE_FORMAT);

/** 연·월만 비교하기 위한 정수 키 */
const getMonthKey = (date) => date.year() * 12 + date.month();

/** 해당 연·월의 1일 moment. month는 0부터 */
const createMonth = (year, month) => moment(`${year}-${String(month + 1).padStart(2, '0')}-01`, DATE_FORMAT);

/** 시각을 제거한 오늘 moment */
const getToday = () => moment().startOf('day');

/**
 * 기간 선택용 달력. 네이티브 input[type=date]를 대체한다.
 * 달력은 스크롤 없는 월 단위 그리드이고, 직접 입력한 값은 확정 시점(blur·Enter)에만
 * 검증해 min/max 범위로 보정하므로 잘못된 날짜가 state로 넘어가지 않는다.
 * 날짜 연산은 moment로 처리하되 원본을 변형하지 않아(clone 후 연산) dayjs로 교체해도 동작이 같다.
 * @param {string} value 선택값 'YYYY-MM-DD' (빈 문자열 허용)
 * @param {(value: string) => void} onChange 값이 확정될 때 호출
 * @param {string} [min] 선택 가능 시작일 'YYYY-MM-DD'
 * @param {string} [max] 선택 가능 종료일 'YYYY-MM-DD'. 지정하면 이후 월로 이동할 수 없다
 * @param {boolean} [disabled] 비활성 여부
 * @param {string} [className] 입력창에 적용할 클래스 (기본 'adm_com_datepicker')
 * @param {string} [placeholder] 값이 없을 때 표시할 문구
 */
function DatePicker({
  value = '',
  onChange,
  min,
  max,
  disabled = false,
  className = 'adm_com_datepicker',
  placeholder = DATE_FORMAT,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPeriodOpen, setIsPeriodOpen] = useState(false); // 연/월 선택 목록 표시 여부
  const [expandedYear, setExpandedYear] = useState(null); // 월이 펼쳐진 연도
  const [viewMonth, setViewMonth] = useState(() => parseDate(value) || getToday());
  const [inputText, setInputText] = useState(value); // 타이핑 중 임시 값
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const expandedYearRef = useRef(null);

  const selectedDate = parseDate(value);
  const minDate = parseDate(min);
  const maxDate = parseDate(max);
  const today = getToday();

  // 값이 없을 때 달력이 미리 잡아줄 기준. min이 더 늦으면 min(=시작일 이후)을 따른다
  const focusBase = useMemo(() => {
    const now = getToday();
    if (minDate && minDate.isAfter(now)) return minDate;
    if (maxDate && maxDate.isBefore(now)) return maxDate;
    return now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max]);
  const focusDate = selectedDate || focusBase;

  // 기간 버튼 등 외부에서 값이 바뀌면 입력창도 따라간다
  useEffect(() => {
    setInputText(value);
  }, [value]);

  // 열 때마다 선택값(없으면 기본 강조 위치) 기준으로 표시 월을 맞춘다
  useEffect(() => {
    if (isOpen) setViewMonth(parseDate(value) || focusBase);
    else setIsPeriodOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 목록을 열면 펼쳐진 연도가 보이도록 스크롤
  useEffect(() => {
    if (isPeriodOpen && expandedYearRef.current) {
      expandedYearRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isPeriodOpen, expandedYear]);

  // 패널은 body로 띄우므로(상위 컨테이너의 overflow:hidden에 잘리지 않도록) 좌표를 직접 잡는다.
  // 첫 프레임이 좌상단에 그려지지 않도록 페인트 전에 실행한다
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const updatePosition = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const isBelowOverflow = rect.bottom + PANEL_HEIGHT > window.innerHeight;
      setPanelPos({
        top: isBelowOverflow ? rect.top - PANEL_HEIGHT - 6 : rect.bottom + 6,
        left: rect.left,
      });
    };
    updatePosition();
    // 스크롤·리사이즈로 입력창이 움직이면 패널도 따라간다
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // 바깥 영역 클릭 시 닫기. 패널이 body에 있어 입력창과 따로 검사한다
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutsideClick = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inWrap && !inPanel) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const isDisabledDate = (date) => {
    if (minDate && date.isBefore(minDate, 'day')) return true;
    if (maxDate && date.isAfter(maxDate, 'day')) return true;
    return false;
  };

  // 첫 주 앞쪽 빈칸(null) + 해당 월의 날짜들
  const cells = useMemo(() => {
    const firstWeekday = viewMonth.clone().startOf('month').day();
    const lastDay = viewMonth.daysInMonth();
    const list = new Array(firstWeekday).fill(null);
    for (let day = 1; day <= lastDay; day += 1) list.push(viewMonth.clone().date(day));
    return list;
  }, [viewMonth]);

  // 연도 목록. min/max가 있으면 그 범위, 없으면 표시 연도 앞뒤로 YEAR_RANGE년
  const years = useMemo(() => {
    const base = viewMonth.year();
    const start = Math.min(minDate ? minDate.year() : base - YEAR_RANGE, base);
    const end = Math.max(maxDate ? maxDate.year() : base + YEAR_RANGE, base);
    const list = [];
    for (let year = start; year <= end; year += 1) list.push(year);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonth, min, max]);

  const canGoPrev = !minDate || getMonthKey(viewMonth) > getMonthKey(minDate);
  const canGoNext = !maxDate || getMonthKey(viewMonth) < getMonthKey(maxDate);

  /** 해당 연·월이 min/max를 벗어나는지 */
  const isDisabledMonth = (year, month) => {
    const key = year * 12 + month;
    if (minDate && key < getMonthKey(minDate)) return true;
    if (maxDate && key > getMonthKey(maxDate)) return true;
    return false;
  };

  const moveMonth = (step) => {
    setViewMonth((current) => current.clone().add(step, 'month'));
  };

  /**
   * 숫자와 하이픈만 허용하고 자동 정렬은 하지 않는다.
   * 값을 재조립하면 커서가 맨 뒤로 밀리므로, 커서 자리에서 그대로 고칠 수 있게 입력을 보존한다.
   */
  const handleInputChange = (e) => {
    setInputText(e.target.value.replace(/[^\d-]/g, ''));
  };

  /**
   * 입력하다 만 값을 날짜로 보정한다.
   * 연도까지만 쳤으면 오늘, 연·월까지 쳤으면 그 달의 마지막 날(이번 달이면 오늘).
   */
  const resolvePartialInput = (digits) => {
    if (digits.length >= 6) {
      const month = Number(digits.slice(4, 6)) - 1;
      if (month >= 0 && month <= 11) {
        const monthStart = createMonth(Number(digits.slice(0, 4)), month);
        return monthStart.isSame(today, 'month') ? today : monthStart.endOf('month');
      }
    }
    if (digits.length >= 4) return today;
    return null;
  };

  /**
   * 입력 확정. 범위를 벗어나면 경계값(보통 오늘)으로 맞춘다.
   * 이전 값으로 되돌리지 않으므로 미래 날짜를 입력해도 그 자리에서 오늘로 바뀐다.
   */
  const commitInput = () => {
    if (inputText === '') {
      if (value !== '') onChange('');
      return;
    }
    // 하이픈이 없거나 위치가 어긋난 입력도 받아준다 ('20260211', '2026-0211' → '2026-02-11')
    const digits = inputText.replace(/\D/g, '');
    const typed = digits.length === 8
      ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`
      : inputText;
    // 완성된 날짜 → 입력하다 만 값 보정 → 그래도 안 되면 선택 가능한 최신 날짜
    const parsed = parseDate(typed) || resolvePartialInput(digits);
    let next = parsed || maxDate || today;
    if (minDate && next.isBefore(minDate, 'day')) next = minDate;
    if (maxDate && next.isAfter(maxDate, 'day')) next = maxDate;
    const nextValue = formatDate(next);
    setInputText(nextValue);
    if (nextValue !== value) onChange(nextValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitInput();
      setIsOpen(false);
    }
  };

  const handleSelect = (date) => {
    onChange(formatDate(date));
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  const handleToday = () => {
    if (isDisabledDate(today)) return;
    handleSelect(today);
  };

  return (
    <div className="adm_com_datepicker_box" ref={wrapRef}>
      <input
        type="text"
        className={className}
        value={inputText}
        placeholder={placeholder}
        disabled={disabled}
        // 네이티브 date input에는 없던 브라우저 입력 히스토리 드롭다운을 막는다
        autoComplete="off"
        inputMode="numeric"
        maxLength={DATE_FORMAT.length}
        onChange={handleInputChange}
        onBlur={commitInput}
        onKeyDown={handleKeyDown}
        onClick={() => !disabled && setIsOpen(true)}
      />
      {isOpen && createPortal(
        <div className="adm_com_datepicker_box__panel" ref={panelRef} style={{ top: panelPos.top, left: panelPos.left }}>
          <div className="adm_com_datepicker_box__head">
            <button
              type="button"
              className={`btn_period ${isPeriodOpen ? 'on' : ''}`}
              onClick={() => {
                if (isPeriodOpen) {
                  setIsPeriodOpen(false);
                  return;
                }
                // 보고 있던 연도를 펼친 채로 열어 월을 바로 고를 수 있게 한다
                setExpandedYear(viewMonth.year());
                setIsPeriodOpen(true);
              }}
            >
              {viewMonth.format('YYYY년 MM월')}
              <i className="arrow" />
            </button>
            <span className="nav_wrap">
              <button type="button" className="nav" aria-label="이전 달" onClick={() => moveMonth(-1)} disabled={!canGoPrev}>&lsaquo;</button>
              <button type="button" className="nav" aria-label="다음 달" onClick={() => moveMonth(1)} disabled={!canGoNext}>&rsaquo;</button>
            </span>
          </div>
          {isPeriodOpen && (
            <div className="adm_com_datepicker_box__years">
              {years.map((year) => (
                <div className="year_item" key={year} ref={year === expandedYear ? expandedYearRef : null}>
                  <button
                    type="button"
                    className={`btn_year ${year === expandedYear ? 'on' : ''}`}
                    onClick={() => setExpandedYear((current) => (current === year ? null : year))}
                  >
                    {year}년
                  </button>
                  {/* 펼침 애니메이션을 주려면 DOM에 남아 있어야 해 클래스로 여닫는다 */}
                  <div className={`month_list ${year === expandedYear ? 'on' : ''}`}>
                    {MONTH_LIST.map((month) => (
                      <button
                        type="button"
                        key={month}
                        className={year === viewMonth.year() && month === viewMonth.month() ? 'selected' : ''}
                        disabled={year !== expandedYear || isDisabledMonth(year, month)}
                        tabIndex={year === expandedYear ? 0 : -1}
                        onClick={() => {
                          setViewMonth(createMonth(year, month));
                          setIsPeriodOpen(false);
                        }}
                      >
                        {month + 1}월
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="adm_com_datepicker_box__week">
            {WEEK_LABELS.map((label) => <span key={label}>{label}</span>)}
          </div>
          <div className="adm_com_datepicker_box__days">
            {cells.map((date, index) => (
              date === null
                ? <span key={`empty_${index}`}>&nbsp;</span>
                : (
                  <button
                    type="button"
                    key={formatDate(date)}
                    className={focusDate.isSame(date, 'day') ? 'selected' : ''}
                    disabled={isDisabledDate(date)}
                    onClick={() => handleSelect(date)}
                  >
                    {date.date()}
                  </button>
                )
            ))}
          </div>
          <div className="adm_com_datepicker_box__foot">
            <button type="button" className="btn_text" onClick={handleClear}>지우기</button>
            <button type="button" className="btn_text" onClick={handleToday} disabled={isDisabledDate(today)}>오늘</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DatePicker;
